import { createInjectionToken } from 'ngxtension/create-injection-token';
import { inject } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';

export interface BreakpointsConfig {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  xsLegacy: string;
  smLegacy: string;
  mdLegacy: string;
  lgLegacy: string;
  xlLegacy: string;
  handset: string;
  tablet: string;
  web: string;
  handsetPortrait: string;
  tabletPortrait: string;
  webPortrait: string;
  handsetLandscape: string;
  tabletLandscape: string;
  webLandscape: string;
}

export const DEFAULT_BREAKPOINTS: BreakpointsConfig = {
  xs: '(width >= 32rem)',
  sm: '(width >= 40rem)',
  md: '(width >= 48rem)',
  lg: '(width >= 64rem)',
  xl: '(width >= 80rem)',
  xsLegacy: '(max-width: 599.98px)',
  smLegacy: '(min-width: 600px)',
  mdLegacy: '(min-width: 960px)',
  lgLegacy: '(min-width: 1280px)',
  xlLegacy: '(min-width: 1920px)',
  handset:
    '(max-width: 599.98px) and (orientation: portrait), (max-width: 959.98px) and (orientation: landscape)',
  tablet:
    '(min-width: 600px) and (max-width: 839.98px) and (orientation: portrait), (min-width: 960px) and (max-width: 1279.98px) and (orientation: landscape)',
  web: '(min-width: 840px) and (orientation: portrait), (min-width: 1280px) and (orientation: landscape)',
  handsetPortrait: '(max-width: 599.98px) and (orientation: portrait)',
  tabletPortrait:
    '(min-width: 600px) and (max-width: 839.98px) and (orientation: portrait)',
  webPortrait: '(min-width: 840px) and (orientation: portrait)',
  handsetLandscape: '(max-width: 959.98px) and (orientation: landscape)',
  tabletLandscape:
    '(min-width: 960px) and (max-width: 1279.98px) and (orientation: landscape)',
  webLandscape: '(min-width: 1280px) and (orientation: landscape)',
};

export type Breakpoint = keyof BreakpointsConfig;

export const [injectBreakpoints, provideBreakpoints] = createInjectionToken(
  () => DEFAULT_BREAKPOINTS,
);

export function injectBreakpointObserver() {
  return inject(BreakpointObserver);
}

export function createBreakpointSignal(breakpoint: Breakpoint) {
  const breakpoints = injectBreakpoints();
  const breakpointObserver = injectBreakpointObserver();
  return toSignal(
    breakpointObserver
      .observe(breakpoints[breakpoint])
      .pipe(map((result) => result.matches)),
    { initialValue: false },
  );
}

export const injectBreakpoint = (breakpoint: Breakpoint) =>
  createBreakpointSignal(breakpoint);
